// src/providers/ridehailing.js
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import storage from 'utils/storage';
import { useNotificationStore } from 'stores/notifications';
import { useAuth } from 'lib/auth';
import { RIDE_HAILING_SOCKET_URL } from 'config';

const RideHailingSocketContext = createContext(null);
export const useRideHailingSocket = () => useContext(RideHailingSocketContext);

export const RideHailingSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const { addNotification } = useNotificationStore();

  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  const token = storage.getToken();
  const reconnectRef = useRef(false);

  /** 🔌 Initialize Ride Hailing socket */
  const initializeSocket = useCallback(() => {
    if (!token) return;

    const path =
      process.env.REACT_APP_SOCKET_ENV === 'development' ? '/socket.io' : '/rh/socket.io';

    const cleanToken = token.replace(/^Bearer\s+/i, '');
    const instance = io(RIDE_HAILING_SOCKET_URL, {
      auth: { token: cleanToken },
      path,
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });

    /** Connection events */
    instance.on('connect', () => {
      console.log('🚗 RideHailing socket connected:', instance.id);
      setConnected(true);
    });

    instance.on('disconnect', (reason) => {
      console.warn('❌ RideHailing socket disconnected:', reason);
      setConnected(false);
      addNotification({
        type: 'warning',
        title: 'Ride Service Disconnected',
        message: reason,
      });
    });

    instance.on('connect_error', (err) => {
      console.error('⚠️ RideHailing socket connect error:', err);
      setConnected(false);
      addNotification({
        type: 'error',
        title: 'Ride Service Failed',
        message: err.message,
      });
    });

    /** --- RIDE EVENTS --- */
    instance.on('ride:new', (ride) => {
      console.log('🆕 ride:new', ride);
      setLastEvent({ event: 'ride:new', data: ride });
    });

    instance.on('ride:cancelled', (ride) => {
      console.log('🚫 ride:cancelled', ride);
      setLastEvent({ event: 'ride:cancelled', data: ride });
    });

    /** --- OFFER EVENTS --- */
    instance.on('offer:update', (data) => {
      console.log('💬 offer:update', data);
      setLastEvent({ event: 'offer:update', data });
    });

    instance.on('offer:accepted', (data) => {
      console.log('🟢 offer:accepted', data);
      setLastEvent({ event: 'offer:accepted', data });
    });

    /** --- TRIP EVENTS --- */
    instance.on('trip:started', (trip) => {
      console.log('🚀 trip:started', trip);
      setLastEvent({ event: 'trip:started', data: trip });
    });

    instance.on('trip:updated', (trip) => {
      console.log('🔄 trip:updated', trip);
      setLastEvent({ event: 'trip:updated', data: trip });
    });

    instance.on('trip:completed', (trip) => {
      console.log('🏁 trip:completed', trip);
      setLastEvent({ event: 'trip:completed', data: trip });
    });

    /** Error handler */
    instance.on('error', (err) => {
      console.error('❌ RideHailing error:', err);
      addNotification({
        type: 'error',
        title: 'Ride Hailing Error',
        message: err.message,
      });
    });

    setSocket(instance);
    reconnectRef.current = true;

    return () => instance.disconnect();
  }, [token, addNotification]);

  /** Initialize when token loads */
  useEffect(() => {
    if (token) initializeSocket();
    return () => socket?.disconnect();
  }, [token]);

  /** EMITTER HELPERS */
  const emitRideCreate = useCallback((payload) => socket?.emit('ride:create', payload), [socket]);

  const emitRideCancel = useCallback((rideId) => socket?.emit('ride:cancel', { rideId }), [socket]);

  const emitOfferSubmit = useCallback((payload) => socket?.emit('offer:submit', payload), [socket]);

  const emitOfferAccept = useCallback(
    (rideRequestId, offerId) => socket?.emit('offer:accept', { rideRequestId, offerId }),
    [socket]
  );

  const emitTripStart = useCallback(
    (rideRequestId) => socket?.emit('trip:start', { rideRequestId }),
    [socket]
  );

  const emitTripUpdate = useCallback(
    (tripId, status) => socket?.emit('trip:update', { tripId, status }),
    [socket]
  );

  const emitTripEnd = useCallback((tripId) => socket?.emit('trip:end', { tripId }), [socket]);

  const value = {
    socket,
    connected,
    lastEvent,

    /** Rider/User actions */
    emitRideCreate,
    emitRideCancel,
    emitOfferSubmit,
    emitOfferAccept,
    emitTripStart,
    emitTripUpdate,
    emitTripEnd,
  };

  return (
    <RideHailingSocketContext.Provider value={value}>{children}</RideHailingSocketContext.Provider>
  );
};
